// Web build entry point.
//
// Order matters: the bridge and editor must be on `window` before any
// renderer script evaluates. Strategy:
//
//   1. Static-import install-bridge + install-editor. These run at
//      module-evaluation time and synchronously attach
//      window.screenplay + window.LastslateEditor.
//   2. Import the renderer CSS via ESM (Vite handles url() rewriting).
//   3. Inject each renderer JS file as a classic <script> tag in order.
//      The renderer source uses classic-script semantics that ESM
//      disallows (e.g. top-level `function` redeclarations in app.js
//      where the last one wins). Loading via <script> preserves
//      Electron-equivalent behavior without touching the renderer
//      source.

import './install-bridge.js';
import './install-editor.js';
import { installObservability } from './observability.js';
import '../renderer/css/vendor/driver.css';
import '../renderer/css/app.css';
import './web-shell.css';

// Wire window-level error capture as early as possible so anything
// thrown during the rest of boot ends up in Sentry (when DSN set) or
// at least in the console with a recognizable prefix.
installObservability();

// Vite's `?url` query returns the asset URL and emits the file
// unchanged in the build. Order in the array IS the load order.
import driverUrl from '../renderer/js/vendor/driver.js.iife.js?url';
import chartUrl from '../renderer/js/chart.js?url';
import markdownUrl from '../renderer/js/markdown.js?url';
import subscriptionUrl from '../renderer/js/subscription.js?url';
import tourRegistryUrl from '../renderer/js/tour/registry.js?url';
import tourRunUrl from '../renderer/js/tour/run.js?url';
import tourStepsUrl from '../renderer/js/tour/steps.js?url';
import tipsRegistryUrl from '../renderer/js/tips/registry.js?url';
import tipsToastUrl from '../renderer/js/tips/toast.js?url';
import tipsDataUrl from '../renderer/js/tips/data.js?url';
import printTitlePageUrl from '../renderer/js/print-title-page.js?url';
import notesUrl from '../renderer/js/notes.js?url';
import appUrl from '../renderer/js/app.js?url';

const RENDERER_SCRIPTS = [
  driverUrl,
  chartUrl,
  markdownUrl,
  subscriptionUrl,
  tourRegistryUrl,
  tourRunUrl,
  tourStepsUrl,
  tipsRegistryUrl,
  tipsToastUrl,
  tipsDataUrl,
  printTitlePageUrl,
  notesUrl,
  appUrl,
];

function loadClassicScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.async = false; // preserve insertion order even though we await
    s.onload = () => resolve(src);
    s.onerror = () => reject(new Error(`failed to load ${src}`));
    document.head.appendChild(s);
  });
}

// Bootstrap an anonymous Supabase session if no real session exists.
// Web's free tier is anonymous-cloud (data lives in the same
// Hocuspocus pipeline as paid users so it's never trapped in one
// browser profile). Real account sign-ins later promote the anon
// user via Supabase's account linking — their work follows them.
//
// We block boot on this so the renderer's first authGetSession()
// in init() sees a valid session and shows the cloud welcome
// instead of the signed-out state.
async function ensureAnonymousSession() {
  const sp = globalThis.screenplay;
  const supabase = sp && sp.__supabase;
  if (!supabase) return; // dev fallback, no Supabase configured
  try {
    const { data } = await supabase.auth.getSession();
    if (data?.session) return; // real or anon session already exists
    const { error } = await supabase.auth.signInAnonymously();
    if (error) {
      console.warn('[lastslate-web] anon sign-in failed:', error.message);
    }
  } catch (err) {
    console.warn('[lastslate-web] ensureAnonymousSession threw:', err && err.message);
  }
}

async function boot() {
  // Seed the auth session BEFORE renderer scripts load. The renderer
  // reads authGetSession() inside init() and decides which welcome
  // state to render based on the result — without this, free users
  // briefly see the signed-out screen flash before the anon session
  // resolves on the next render.
  await ensureAnonymousSession();

  for (const url of RENDERER_SCRIPTS) {
    // Sequential by design — each script may depend on globals
    // installed by the previous one (chart/markdown -> app.js).

    await loadClassicScript(url);
  }
  // The renderer's `init()` is wired to `DOMContentLoaded`
  // (src/renderer/js/app.js:203). That event already fired by the
  // time our classic scripts attach their listener, so without this
  // re-dispatch init() never runs and no UI handlers get bound.
  if (document.readyState !== 'loading') {
    document.dispatchEvent(new Event('DOMContentLoaded'));
  }
  wireOfflineOverlay();
  // Web-shell chrome: in-app menu bar, drag-drop import, file
  // picker, browser notifications. All additive; the renderer
  // doesn't know it's there.
  const { installWebShell } = await import('./web-shell.js');
  installWebShell();
  // Tier gate intentionally disabled — free users get the same
  // editor + local-storage feature set as Electron's free tier.
  // Cloud sync / AI are gated per-feature by the renderer's existing
  // ensureTier checks, not by a full-page paywall.
  // Service worker for PWA installability + file_handlers. Registered
  // last so a SW failure doesn't block the editor from booting.
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register('/sw.js').catch((err) => {
      console.warn('[lastslate-web] service worker registration failed', err);
    });
  }
}

function wireOfflineOverlay() {
  const overlay = document.getElementById('offline-overlay');
  if (!overlay) return;
  const sync = () => overlay.classList.toggle('is-visible', !navigator.onLine);
  window.addEventListener('online', sync);
  window.addEventListener('offline', sync);
  sync();
}

boot().catch((err) => {
  console.error('[lastslate-web] boot failed', err);
});
